import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import QuickViewSection from '../../components/QuickViewSection';
import AppointmentCard from '../AppointmentCard';
import {
  getAppointmentsConfig,
  getClientAppointments,
  APPOINTMENTS_STATUS,
} from '../../../../../../services/appointments';
import AddEditAppointment from '../AddEditAppointment';
import useSearchParams from '../../../../../../hooks/useSearchParams';

const ClientAppointments = ({ clientId, allAssoc = [], setViewAllType }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const { getValue: getSearchParam } = useSearchParams();
  const appointmentIdParam = getSearchParam('appointmentId');
  const appointmentModalParam = getSearchParam('appointmentModal');
  const queryClient = useQueryClient();

  const { data, isFetching, refetch } = useQuery(
    ['client-appointments', clientId],
    async () => {
      try {
        const query = {
          limit: 5,
          offset: 0,
          statuses: [APPOINTMENTS_STATUS.REQUESTED, APPOINTMENTS_STATUS.ASSIGNED],
        };
        const { data: appointments } = await getClientAppointments(clientId, query);
        return appointments;
      } catch (error) {
        console.error('Error fetching client appointments:', error);
        return [];
      }
    },
    {
      enabled: !!clientId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const handleAddAppointment = useCallback(() => {
    refetch();
    queryClient.refetchQueries(['clientRecentAppointments', clientId]);
  }, [queryClient]);

  const handleEditAppointment = useCallback(appointment => {
    setEditAppointment(appointment);
    setShowAddModal(true);
  }, []);

  const { data: appointmentsConfig } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        stores: [],
        appointmentTypes: [],
      },
    },
  );

  const handleCloseAddAppointment = useCallback(() => {
    setShowAddModal(false);
    setEditAppointment(null);
  }, []);

  useEffect(() => {
    if (data?.length > 0) {
      if (appointmentIdParam && appointmentModalParam === 'true') {
        const appointmentToOpen = data?.find(appointment => appointment.id.toString() === appointmentIdParam);
        if (appointmentToOpen) {
          handleEditAppointment(appointmentToOpen);
        }
      }
    }
  }, [data, appointmentIdParam, appointmentModalParam]);

  return (
    <QuickViewSection
      title="Appointments"
      onViewAllClick={() => {
        setViewAllType('allAppointments');
      }}
      onAddClick={() => setShowAddModal(true)}
      isLoading={isFetching}
    >
      {data?.map(appointment => (
        <AppointmentCard
          key={appointment.id}
          {...appointment}
          allAssoc={allAssoc}
          onAssignedSuccess={() => {
            refetch();
            queryClient.refetchQueries(['clientRecentAppointments', clientId]);
          }}
          handleEdit={() => handleEditAppointment(appointment)}
          appointmentsConfig={appointmentsConfig}
        />
      ))}
      {data?.length === 0 && !isFetching && (
        <div className="qv-section-empty">No Appointments</div>
      )}
      <AddEditAppointment
        isOpen={showAddModal}
        clientId={clientId}
        onClose={handleCloseAddAppointment}
        onSuccess={handleAddAppointment}
        allAssoc={allAssoc}
        appointment={editAppointment}
      />
    </QuickViewSection>
  );
};

export default ClientAppointments;
